// ─────────────────────────────────────────────
// KLOTHIQ — Cashbook Historical Import Wizard (TASK-FE-014, TASK-FE-015)
// ─────────────────────────────────────────────

import { Input, Select } from '@/components/ui/FormControls';
import DatePicker from '@/components/common/DatePicker';
import { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Link from '@/components/common/AccessLink';
import * as XLSX from 'xlsx';
import {
  UploadCloud,
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Calendar,
  Loader2,
  History,
  Undo2,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirm } from '@/contexts/ConfirmContext';
import {
  usePartners,
  useExpenseCategories,
  useImportBatches,
  useImportCashbook,
  useVoidImportBatch,
  ImportBatch,
} from '@/hooks/useAccounts';
import { useCashAccounts } from '@/hooks/useCash';
import PageHeader from '@/components/common/PageHeader';
import PartnerFormModal from './components/PartnerFormModal';
import {
  detectColumns,
  extractRows,
  classifyRow,
  evaluateRows,
  previewTotals,
  toImportPayload,
  fileHash,
  unmatchedPartnerHints,
  PreviewRow,
  EvaluatedRow,
} from '@/utils/cashbookImport';
import {
  formatINR,
  formatDisplayDate,
  parseRpcError,
  toISODate,
} from '@/utils/cashbookCalc';

type Step = 'upload' | 'review' | 'confirm' | 'batches';

export default function CashbookImportPage() {
  const navigate = useNavigate();
  const { role } = useAuth();
  const confirm = useConfirm();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Queries & Mutations
  const { data: partners } = usePartners(true);
  const { data: categories } = useExpenseCategories(true);
  const { data: accounts } = useCashAccounts();
  const { data: batches, refetch: refetchBatches } = useImportBatches();
  const importMutation = useImportCashbook();
  const voidMutation = useVoidImportBatch();

  // Wizard State
  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState('');
  const [computedHash, setComputedHash] = useState('');
  const [sheetTotals, setSheetTotals] = useState<{ income: number; expense: number; balance: number } | null>(null);
  const [rawPreviewRows, setRawPreviewRows] = useState<PreviewRow[]>([]);
  const [cutoffDate, setCutoffDate] = useState<string | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Partner creation modal state from wizard
  const [isAddPartnerOpen, setIsAddPartnerOpen] = useState(false);
  const [quickPartnerName, setQuickPartnerName] = useState('');

  // Role Gate check
  const isOwnerOrAdmin = role === 'owner' || role === 'admin';
  if (!isOwnerOrAdmin) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
        <h2 className="mt-4 text-lg font-bold text-foreground">Access Restricted</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Only Business Owners and Administrators can import historical cashbook sheets.
        </p>
        <Link
          to="/accounts"
          className="mt-4 inline-block rounded-md bg-primary px-4 py-2 text-xs font-medium text-primary-foreground"
        >
          Return to Accounts
        </Link>
      </div>
    );
  }

  // Check duplicate hash
  const existingBatch = useMemo(() => {
    if (!computedHash || !batches) return null;
    return batches.find((b) => b.file_hash === computedHash && !b.is_void) ?? null;
  }, [computedHash, batches]);

  // Evaluated rows using current cutoff & partners/categories
  const evaluatedRows: EvaluatedRow[] = useMemo(() => {
    if (!rawPreviewRows.length) return [];
    return evaluateRows(rawPreviewRows, {
      today: toISODate(new Date()),
      cutoff: cutoffDate,
    });
  }, [rawPreviewRows, cutoffDate]);

  // Live preview totals
  const totals = useMemo(() => {
    return previewTotals(evaluatedRows);
  }, [evaluatedRows]);

  // Missing partners hints
  const missingPartners = useMemo(() => {
    return unmatchedPartnerHints(rawPreviewRows);
  }, [rawPreviewRows]);

  // ── Handle File Upload ───────────────────────────────────────
  const handleFileUpload = async (file: File) => {
    try {
      const buffer = await file.arrayBuffer();
      const hash = await fileHash(buffer);
      setFileName(file.name);
      setComputedHash(hash);

      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      if (!firstSheetName) {
        toast.error('The selected Excel file is empty');
        return;
      }

      const worksheet = workbook.Sheets[firstSheetName];
      if (!worksheet) {
        toast.error('The selected sheet could not be read');
        return;
      }
      const sheetAoa = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as (string | number)[][];

      const cols = detectColumns(sheetAoa);
      if (!cols) {
        toast.error('Could not detect cashbook columns. Required: Date, Particular, and Income/Expense columns.');
        return;
      }

      const extracted = extractRows(sheetAoa, cols.headerRow, cols.map);
      if (extracted.rows.length === 0) {
        toast.error('No transaction rows detected in the sheet.');
        return;
      }

      if (extracted.sheetTotals) {
        setSheetTotals({
          income: extracted.sheetTotals.income ?? 0,
          expense: extracted.sheetTotals.expense ?? 0,
          balance: extracted.sheetTotals.balance ?? 0,
        });
      } else {
        setSheetTotals(null);
      }

      // Classify initial rows
      const classified = extracted.rows.map((r) =>
        classifyRow(r, partners ?? [], categories ?? [])
      );

      setRawPreviewRows(classified);
      setStep('review');
      toast.success(`Loaded ${classified.length} rows from "${file.name}"`);
    } catch (err) {
      console.error('Failed to parse sheet:', err);
      toast.error('Failed to parse spreadsheet file. Please check file format.');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  // ── Row updates in review grid ──────────────────────────────
  const handleUpdateRow = (index: number, updates: Partial<PreviewRow>) => {
    setRawPreviewRows((prev) => {
      const next = [...prev];
      const target = next[index];
      if (!target) return prev;
      next[index] = { ...target, ...updates };
      return next;
    });
  };

  // ── Handle Final Submit ──────────────────────────────────────
  const handleConfirmImport = async () => {
    if (!selectedAccountId) {
      toast.error('Please select an account to deposit/credit these entries');
      return;
    }
    if (!totals.canImport) {
      toast.error(`Cannot import: ${totals.counts.needs_fix} rows require correction`);
      return;
    }

    const payloadRows = toImportPayload(evaluatedRows);
    if (payloadRows.length === 0) {
      toast.error('No importable rows available after applying filters and cut-off');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await importMutation.mutateAsync({
        batch: {
          file_name: fileName,
          file_hash: computedHash,
          row_count: payloadRows.length,
          total_income: totals.income,
          total_expense: totals.expense,
          cutoff_date: cutoffDate || null,
          account_id: selectedAccountId,
        },
        rows: payloadRows,
      });

      toast.success(`Successfully imported ${res.inserted} entries into cashbook!`);
      navigate('/accounts/cashbook');
    } catch (err) {
      const parsed = parseRpcError(err);
      toast.error(parsed.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Handle Void Batch ────────────────────────────────────────
  const handleVoidBatch = async (batch: ImportBatch) => {
    const isOk = await confirm({
      title: 'Void Import Batch',
      description: `Are you sure you want to void batch "${batch.file_name}" (${batch.row_count} rows)? This will reverse all ledger balances for this import.`,
      variant: 'destructive',
      confirmText: 'Void Entire Batch',
    });
    if (!isOk) return;

    try {
      await voidMutation.mutateAsync({ batch_id: batch.id });
      toast.success('Import batch voided and balances reversed');
      refetchBatches();
    } catch (err) {
      const parsed = parseRpcError(err);
      toast.error(parsed.message);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Import Cashbook Sheet"
        description="Import historical accounts and ledger sheets into the system"
        backTo="/accounts/cashbook"
        actions={
          <button
            type="button"
            onClick={() => setStep(step === 'batches' ? 'upload' : 'batches')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground shadow-xs hover:bg-muted"
          >
            <History className="h-3.5 w-3.5" />
            <span>{step === 'batches' ? 'Import New File' : 'Import History'}</span>
          </button>
        }
      />

      {/* Wizard Step Indicator (unless viewing batches) */}
      {step !== 'batches' && (
        <div className="flex items-center justify-between border-b border-border pb-4">
          <div className="flex items-center gap-2">
            <span
              className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                step === 'upload' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}
            >
              1
            </span>
            <span className={`text-xs font-medium ${step === 'upload' ? 'text-foreground' : 'text-muted-foreground'}`}>
              Upload Sheet
            </span>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground/40" />
          <div className="flex items-center gap-2">
            <span
              className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                step === 'review' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}
            >
              2
            </span>
            <span className={`text-xs font-medium ${step === 'review' ? 'text-foreground' : 'text-muted-foreground'}`}>
              Review & Map ({rawPreviewRows.length} rows)
            </span>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground/40" />
          <div className="flex items-center gap-2">
            <span
              className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                step === 'confirm' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}
            >
              3
            </span>
            <span className={`text-xs font-medium ${step === 'confirm' ? 'text-foreground' : 'text-muted-foreground'}`}>
              Select Account & Confirm
            </span>
          </div>
        </div>
      )}

      {/* ── STEP 1: UPLOAD ────────────────────────────────────────── */}
      {step === 'upload' && (
        <div className="mx-auto max-w-xl space-y-4">
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className="obsidian-surface flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-card/60 p-12 text-center transition-colors hover:border-primary/50 hover:bg-card cursor-pointer shadow-xs"
          >
            <Input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.[0]) handleFileUpload(e.target.files[0]);
              }}
              helpLabel="Spreadsheet file"
            />
            <div className="rounded-full bg-primary/10 p-3 text-primary">
              <UploadCloud className="h-8 w-8" />
            </div>
            <h3 className="mt-4 text-sm font-semibold text-foreground">
              Click to select or drag and drop your cashbook Excel file
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Supports .xlsx, .xls, .csv with Date, Particular, Income and Expense columns
            </p>
          </div>

          <div className="obsidian-surface rounded-xl border border-border bg-card p-4 text-xs text-muted-foreground space-y-2">
            <p className="font-semibold text-foreground">Expected Sheet Format:</p>
            <div className="grid grid-cols-5 gap-2 border border-border/60 rounded-md p-2 bg-muted/20 font-mono text-[11px] text-center">
              <div>Date</div>
              <div>Particular</div>
              <div>Income</div>
              <div>Expense</div>
              <div>A/C Balance</div>
            </div>
            <p>
              • Sales, Purchases, Partner Capital (e.g. <code>NANDU(CAPITAL)</code>), and operating expenses
              will be categorized automatically.
            </p>
          </div>
        </div>
      )}

      {/* ── STEP 2: REVIEW & MAP ──────────────────────────────────── */}
      {step === 'review' && (
        <div className="space-y-4">
          {/* Duplicate hash warning if applicable */}
          {existingBatch && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-amber-900 dark:text-amber-200 text-xs">
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
              <div>
                <p className="font-semibold">Duplicate File Warning</p>
                <p className="mt-0.5">
                  An identical file was previously imported on{' '}
                  {formatDisplayDate(existingBatch.created_at)} ({existingBatch.row_count} rows).
                  Proceeding may duplicate ledger records unless you void the prior batch first.
                </p>
              </div>
            </div>
          )}

          {/* Missing partner hints banner */}
          {missingPartners.length > 0 && (
            <div className="flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-primary shrink-0" />
                <span>
                  Unmatched partner capital names found in sheet:{' '}
                  <strong className="text-foreground">{missingPartners.join(', ')}</strong>.
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setQuickPartnerName(missingPartners[0] || '');
                  setIsAddPartnerOpen(true);
                }}
                className="rounded bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
              >
                + Add Partner "{missingPartners[0]}"
              </button>
            </div>
          )}

          {/* Cut-off Date Picker & Controls (Q-001) */}
          <div className="obsidian-surface flex flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-xs sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-primary" />
                <span className="text-xs font-semibold text-foreground">
                  Cut-off Date for Live Sales & Purchases
                </span>
              </div>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                Skip system-tracked sales/purchases on or after this date. Capital and Expenses are always kept.
              </p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <DatePicker
                aria-label="Import cutoff date"
                value={cutoffDate || ''}
                onChange={(nextDate) => setCutoffDate((nextDate ?? '') || null)}
              />
              {cutoffDate && (
                <button
                  type="button"
                  onClick={() => setCutoffDate(null)}
                  className="min-h-11 shrink-0 rounded-xl border border-border px-3 text-sm text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  Clear cut-off
                </button>
              )}
            </div>
          </div>

          {/* Live Reconciliation Totals Bar (AC-018) */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6 text-xs">
            <div className="obsidian-surface rounded-lg border border-border bg-card p-3 shadow-xs">
              <span className="text-muted-foreground">Importable Inflow</span>
              <p className="mt-1 font-mono font-bold text-emerald-600 dark:text-emerald-400 text-sm">
                +{formatINR(totals.income)}
              </p>
            </div>
            <div className="obsidian-surface rounded-lg border border-border bg-card p-3 shadow-xs">
              <span className="text-muted-foreground">Importable Outflow</span>
              <p className="mt-1 font-mono font-bold text-rose-600 dark:text-rose-400 text-sm">
                -{formatINR(totals.expense)}
              </p>
            </div>
            <div className="obsidian-surface rounded-lg border border-border bg-card p-3 shadow-xs">
              <span className="text-muted-foreground">Net Ledger Change</span>
              <p className="mt-1 font-mono font-bold text-foreground text-sm">
                {formatINR(totals.balance)}
              </p>
            </div>
            <div className="obsidian-surface rounded-lg border border-border bg-card p-3 shadow-xs">
              <span className="text-muted-foreground">Capital Detected</span>
              <p className="mt-1 font-mono font-bold text-primary text-sm">
                {formatINR(totals.capital)}
              </p>
            </div>
            <div className="obsidian-surface rounded-lg border border-border bg-card p-3 shadow-xs">
              <span className="text-muted-foreground">Purchases Detected</span>
              <p className="mt-1 font-mono font-bold text-amber-600 dark:text-amber-400 text-sm">
                {formatINR(totals.purchases)}
              </p>
            </div>
            <div className="obsidian-surface rounded-lg border border-border bg-card p-3 shadow-xs">
              <span className="text-muted-foreground">Status Counts</span>
              <p className="mt-1 text-[11px]">
                <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{totals.counts.ok} OK</span>
                {totals.counts.needs_fix > 0 && (
                  <span className="ml-1 text-destructive font-semibold">· {totals.counts.needs_fix} Fix</span>
                )}
                {totals.counts.skipped > 0 && (
                  <span className="ml-1 text-muted-foreground">· {totals.counts.skipped} Skip</span>
                )}
              </p>
            </div>
          </div>

          {/* Sheet Total Comparison Badge if found */}
          {sheetTotals && (
            <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-2.5 text-xs">
              <span className="text-muted-foreground">
                Excel Sheet Totals Row: Income {formatINR(sheetTotals.income)} · Expense {formatINR(sheetTotals.expense)} · Balance {formatINR(sheetTotals.balance)}
              </span>
              {Math.abs(sheetTotals.income - totals.income) < 1 &&
              Math.abs(sheetTotals.expense - totals.expense) < 1 ? (
                <span className="inline-flex items-center gap-1 font-medium text-emerald-600 dark:text-emerald-400">
                  <Check className="h-3.5 w-3.5" /> Golden Match!
                </span>
              ) : (
                <span className="text-muted-foreground text-[11px]">
                  (Difference due to cut-off / exclusions)
                </span>
              )}
            </div>
          )}

          {/* Editable Preview Table */}
          <div className="obsidian-surface overflow-hidden rounded-xl border border-border bg-card shadow-xs">
            <div className="overflow-x-auto max-h-[500px]">
              <table className="w-full text-left text-xs">
                <thead className="sticky top-0 z-10 border-b border-border bg-muted/90 backdrop-blur-xs font-medium text-muted-foreground">
                  <tr>
                    <th className="py-2 px-2.5 w-8">#</th>
                    <th className="py-2 px-2.5">Status</th>
                    <th className="py-2 px-2.5">Date</th>
                    <th className="py-2 px-2.5">Particular</th>
                    <th className="py-2 px-2.5">Kind</th>
                    <th className="py-2 px-2.5">Partner / Category</th>
                    <th className="py-2 px-2.5 text-right">Income (₹)</th>
                    <th className="py-2 px-2.5 text-right">Expense (₹)</th>
                    <th className="py-2 px-2.5 text-center">Exclude</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {evaluatedRows.map((r, idx) => {
                    const statusClass =
                      r.status === 'ok'
                        ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                        : r.status === 'needs_fix'
                        ? 'bg-destructive/10 text-destructive'
                        : r.status === 'needs_review'
                        ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                        : 'bg-muted text-muted-foreground';

                    return (
                      <tr
                        key={idx}
                        className={`hover:bg-muted/20 ${r.status === 'skipped' ? 'opacity-50' : ''}`}
                      >
                        <td className="py-2 px-2.5 text-muted-foreground font-mono text-[11px]">
                          {r.row_no}
                        </td>
                        <td className="py-2 px-2.5 whitespace-nowrap">
                          <span
                            className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium capitalize ${statusClass}`}
                            title={r.issues.join('; ')}
                          >
                            {r.status.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="py-2 px-2.5">
                          <DatePicker
                            className="min-w-[170px]"
                            aria-label={`Transaction ${idx + 1} date`}
                            value={r.date || ''}
                            onChange={(nextDate) => handleUpdateRow(idx, { date: nextDate ?? '' })}
                          />
                        </td>
                        <td className="py-2 px-2.5 font-medium text-foreground min-w-[160px]">
                          <Input
                            type="text"
                            value={r.particular}
                            onChange={(e) => handleUpdateRow(idx, { particular: e.target.value })}
                            className="h-6 w-full rounded border border-border bg-background px-1.5 text-xs text-foreground"
                            helpLabel="Transaction description"
                          />
                        </td>
                        <td className="py-2 px-2.5">
                          <Select
                            value={r.kind || ''}
                            onChange={(e) =>
                              handleUpdateRow(idx, {
                                kind: (e.target.value as any) || null,
                              })
                            }
                            className="h-6 rounded border border-border bg-background px-1 text-xs text-foreground capitalize"
                            helpLabel="Transaction type"
                          >
                            <option value="sale">Sale</option>
                            <option value="purchase">Purchase</option>
                            <option value="capital">Capital</option>
                            <option value="expense">Expense</option>
                          </Select>
                        </td>
                        <td className="py-2 px-2.5">
                          {r.kind === 'capital' ? (
                            <Select
                              value={r.partner_id || ''}
                              onChange={(e) => handleUpdateRow(idx, { partner_id: e.target.value || null })}
                              className="h-6 rounded border border-border bg-background px-1 text-xs text-foreground max-w-[140px]"
                              helpLabel="Capital partner"
                            >
                              <option value="">Select partner</option>
                              {partners?.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name}
                                </option>
                              ))}
                            </Select>
                          ) : r.kind === 'expense' ? (
                            <Select
                              value={r.expense_category_id || ''}
                              onChange={(e) =>
                                handleUpdateRow(idx, {
                                  expense_category_id: e.target.value || null,
                                })
                              }
                              className="h-6 rounded border border-border bg-background px-1 text-xs text-foreground max-w-[140px]"
                              helpLabel="Expense category"
                            >
                              <option value="">Miscellaneous</option>
                              {categories?.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </Select>
                          ) : (
                            <span className="text-muted-foreground text-[11px]">
                              {r.supplier_hint || '—'}
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-2.5 text-right font-mono text-emerald-600 dark:text-emerald-400">
                          {r.side === 'income' ? formatINR(r.amount) : '—'}
                        </td>
                        <td className="py-2 px-2.5 text-right font-mono text-rose-600 dark:text-rose-400">
                          {r.side === 'expense' ? formatINR(r.amount) : '—'}
                        </td>
                        <td className="py-2 px-2.5 text-center">
                          <Input
                            type="checkbox"
                            checked={r.excluded}
                            onChange={(e) => handleUpdateRow(idx, { excluded: e.target.checked })}
                            className="h-3.5 w-3.5 rounded border-border text-primary focus:ring-0"
                            title="Exclude this row from import"
                            helpLabel="Exclude row from import"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Review Step Footer Controls */}
          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              onClick={() => setStep('upload')}
              className="rounded-md border border-border bg-background px-4 py-2 text-xs font-medium text-foreground hover:bg-muted"
            >
              Back to Upload
            </button>
            <button
              type="button"
              disabled={!totals.canImport}
              onClick={() => setStep('confirm')}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              <span>Proceed to Account Selection</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3: SELECT ACCOUNT & CONFIRM ──────────────────────── */}
      {step === 'confirm' && (
        <div className="obsidian-surface mx-auto max-w-lg space-y-5 rounded-xl border border-border bg-card p-6 shadow-xs">
          <div>
            <h3 className="text-base font-semibold text-foreground">Select Destination Account</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Select which cash or bank account will reflect the net balance change of this historical ledger.
            </p>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">
              Cash / Bank Account <span className="text-destructive">*</span>
            </label>
            <Select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
              disabled={isSubmitting}
              helpLabel="Cash / Bank Account *"
            >
              <option value="">Select account</option>
              {accounts?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.account_type}) — Current Balance: ₹
                  {Number(a.current_balance).toLocaleString('en-IN')}
                </option>
              ))}
            </Select>
          </div>

          {/* Import Summary Confirmation Card */}
          <div className="rounded-lg border border-border bg-muted/20 p-4 text-xs space-y-2.5">
            <div className="flex justify-between border-b border-border/40 pb-2">
              <span className="text-muted-foreground">File Name</span>
              <span className="font-medium text-foreground">{fileName}</span>
            </div>
            <div className="flex justify-between border-b border-border/40 pb-2">
              <span className="text-muted-foreground">Rows to Import</span>
              <span className="font-semibold text-foreground">{evaluatedRows.filter((r) => r.status === 'ok').length}</span>
            </div>
            <div className="flex justify-between border-b border-border/40 pb-2">
              <span className="text-muted-foreground">Total Income Credited</span>
              <span className="font-mono font-medium text-emerald-600 dark:text-emerald-400">+{formatINR(totals.income)}</span>
            </div>
            <div className="flex justify-between border-b border-border/40 pb-2">
              <span className="text-muted-foreground">Total Expenses Debited</span>
              <span className="font-mono font-medium text-rose-600 dark:text-rose-400">-{formatINR(totals.expense)}</span>
            </div>
            <div className="flex justify-between font-bold pt-1">
              <span>Net Account Balance Impact</span>
              <span className="font-mono text-sm">{formatINR(totals.balance)}</span>
            </div>
          </div>

          <div className="flex items-center justify-between pt-3">
            <button
              type="button"
              onClick={() => setStep('review')}
              disabled={isSubmitting}
              className="rounded-md border border-border bg-background px-4 py-2 text-xs font-medium text-foreground hover:bg-muted"
            >
              Back to Review
            </button>
            <button
              type="button"
              disabled={!selectedAccountId || isSubmitting}
              onClick={handleConfirmImport}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Confirm & Import Ledger
            </button>
          </div>
        </div>
      )}

      {/* ── BATCHES / IMPORT HISTORY (TASK-FE-015) ────────────────── */}
      {step === 'batches' && (
        <div className="space-y-4">
          <div className="obsidian-surface rounded-xl border border-border bg-card p-4 shadow-xs">
            <h3 className="text-sm font-semibold text-foreground">Import Batches</h3>
            <p className="text-xs text-muted-foreground">
              History of uploaded spreadsheets with one-click atomic reversal
            </p>
          </div>

          {batches && batches.length > 0 ? (
            <div className="obsidian-surface overflow-hidden rounded-xl border border-border bg-card shadow-xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="border-b border-border bg-muted/40 font-medium text-muted-foreground">
                    <tr>
                      <th className="py-2.5 px-3">Date</th>
                      <th className="py-2.5 px-3">File Name</th>
                      <th className="py-2.5 px-3 text-center">Rows</th>
                      <th className="py-2.5 px-3 text-right">Income (₹)</th>
                      <th className="py-2.5 px-3 text-right">Expense (₹)</th>
                      <th className="py-2.5 px-3">Status</th>
                      <th className="py-2.5 px-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {batches.map((b) => (
                      <tr key={b.id} className="hover:bg-muted/30">
                        <td className="py-2.5 px-3 text-muted-foreground font-mono">
                          {formatDisplayDate(b.created_at)}
                        </td>
                        <td className="py-2.5 px-3 font-medium text-foreground">
                          {b.file_name}
                        </td>
                        <td className="py-2.5 px-3 text-center font-mono">
                          {b.row_count}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-emerald-600 dark:text-emerald-400">
                          +{formatINR(Number(b.total_income))}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-rose-600 dark:text-rose-400">
                          -{formatINR(Number(b.total_expense))}
                        </td>
                        <td className="py-2.5 px-3">
                          {b.is_void ? (
                            <span className="rounded bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                              Voided
                            </span>
                          ) : (
                            <span className="rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 text-[10px] font-medium">
                              Active
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-right">
                          {!b.is_void && (
                            <button
                              type="button"
                              onClick={() => handleVoidBatch(b)}
                              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
                              title="Void this import batch and reverse its balances"
                            >
                              <Undo2 className="h-3 w-3" />
                              Void Batch
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="obsidian-surface rounded-xl border border-border bg-card p-12 text-center text-xs text-muted-foreground">
              No import batches recorded yet.
            </div>
          )}
        </div>
      )}

      {/* Quick Add Partner Modal */}
      <PartnerFormModal
        isOpen={isAddPartnerOpen}
        onClose={() => setIsAddPartnerOpen(false)}
        partner={quickPartnerName ? ({ name: quickPartnerName, share_percent: 0, is_active: true } as any) : null}
      />
    </div>
  );
}
